import React, { useCallback } from 'react';
import type { InternalTableSchema, InternalColumnDef, InternalActionDef } from '../schema/types';
import type { PluginRegistry } from '../plugins/PluginRegistry';
import type { PinOffsets } from './computePinOffsets';
import type { GroupedRow } from './useGroupedData';
import { isGroupRow } from './useGroupedData';
import type { VirtualRowGroup } from './useVirtualRows';
import { RowRenderer } from './RowRenderer';
import { GroupRowRenderer } from './GroupRowRenderer';
import { VirtualTbody } from './VirtualTbody';
import { isRowSelectable } from '../../utils/rowSelection';
import type { InjectedDataMap } from '../../hooks/useDataInjectionV2';
import { EmptyState } from '@packages/ui/layout/EmptyState';
import { TABLE_IDS } from '../semantic-contract';

function SkeletonRow({
  colSpan,
  columns,
  showCheckbox,
  showActions,
  height,
}: {
  colSpan: number;
  columns: ReadonlyArray<InternalColumnDef>;
  showCheckbox: boolean;
  showActions: boolean;
  height: number;
}) {
  return (
    <tr style={{ height }} className='dt-v2-skeleton-row'>
      {showCheckbox && (
        <td style={{ width: 40 }}>
          <div className='dt-v2-skeleton-cell' style={{ width: 16, height: 16, borderRadius: 2 }} />
        </td>
      )}
      {columns.map((col, idx) => (
        <td key={col.field ?? idx}>
          <div
            className='dt-v2-skeleton-cell'
            style={{ width: '75%', height: 14, borderRadius: 4 }}
          />
        </td>
      ))}
      {showActions && (
        <td style={{ width: 80 }}>
          <div className='dt-v2-skeleton-cell' style={{ width: 24, height: 14, borderRadius: 4 }} />
        </td>
      )}
    </tr>
  );
}

interface RowRenderSharedProps {
  visibleColumns: ReadonlyArray<InternalColumnDef>;
  registry: PluginRegistry;
  namespace: string;
  showSelectionColumn: boolean;
  showActionsColumn: boolean;
  effectiveSelectedKeys: string[];
  internalSchema: InternalTableSchema;
  isGroupingEnabled: boolean;
  effectiveSubRowActions: ReadonlyArray<InternalActionDef>;
  hasEditableColumns: boolean;
  subRowConfig: InternalTableSchema['features']['rows']['subRows'];
  subRowColumns: InternalTableSchema['subRowColumns'];
  tableState: {
    editingRows: Map<string, Record<string, unknown>>;
    editErrors: Map<string, Record<string, string>>;
    serverErrors: Map<string, Record<string, string>>;
    expandedRows: Set<string>;
    columnSizes: Record<string, number>;
  };
  injectedData: InjectedDataMap;
  subRowInjectedData: InjectedDataMap;
  keyField: string;
  pinOffsets: PinOffsets;
  editingSubRows: Map<string, Record<string, unknown>>;
  subRowEditErrors: Map<string, Record<string, string>>;
  effectiveSaveLoading: boolean;
  rowStyleFn?: (row: Record<string, unknown>, index: number) => Record<string, string | number> | undefined;

  resolveRowKey: (row: Record<string, unknown>, rowIndex: number) => string;
  resolveReactKey: (row: Record<string, unknown>, rowIndex: number) => string;
  handleRowSelect: (rowKey: string, checked: boolean) => void;
  handleRowActionExec: (
    action: InternalActionDef,
    row: Record<string, unknown>,
    subRowContext?: any
  ) => Promise<void>;
  checkPermission?: (perm: { action: string; resource: string }) => boolean;
  handleCellDoubleClick?: (rowKey: string, field: string) => void;
  handleEditChange: (rowKey: string, field: string, value: unknown) => void;
  handleEditBlur: (rowKey: string, field: string) => void;
  handleSubRowCellDoubleClick: (parentKey: string, subIndex: number, field: string) => void;
  handleSubRowEditChange: (
    parentKey: string,
    subIndex: number,
    field: string,
    value: unknown
  ) => void;
  handleSubRowEditBlur: (parentKey: string, subIndex: number, field: string) => void;
  handleToggleRowExpand: (rowKey: string) => void;
  handleRowSave: (rowKey: string) => Promise<void>;
  handleRowCancel: (rowKey: string) => void;
  externalEditControl?: boolean;
  stateActions: { toggleGroup: (key: string) => void };
  /**
   * Number of extra columns sub-rows need beyond the parent's data column budget.
   * Parent rows render empty filler cells for these so total <td> counts match.
   */
  subRowOverflowCols: number;
  /** Called when a row is clicked (for block-event publishing). */
  onRowClick?: (row: Record<string, unknown>, rowKey: string) => void;
}

export interface TableBodyRendererProps extends RowRenderSharedProps {
  renderData: ReadonlyArray<Record<string, unknown> | GroupedRow>;
  paginatedData: unknown[];
  topPinnedRows: unknown[];
  bottomPinnedRows: unknown[];
  shouldVirtualize: boolean;
  virtualRows: any;
  groupByConfig: InternalTableSchema['features']['groupBy'];
  groupAlwaysExpanded: boolean;
  selection: InternalTableSchema['features']['selection'];
  /** When true, the table is in random-access virtual scroll mode */
  isRandomAccessMode?: boolean;
  /** Total records known from random-access data layer */
  randomAccessTotalRecords?: number;
  /** When true, shows empty filter message instead of "No Data Found" */
  isAwaitingFilter?: boolean;
  /** Custom message when awaiting filter */
  emptyFilterMessage?: string;
}

function renderSingleRow(
  row: Record<string, unknown>,
  rawRec: Record<string, unknown>,
  rowIndex: number,
  props: RowRenderSharedProps
) {
  const rowKey = props.resolveRowKey(rawRec, rowIndex);
  const reactKey = props.resolveReactKey(rawRec, rowIndex);
  const editingRow = props.tableState.editingRows.get(rowKey) ?? null;
  const _clientErrors = props.tableState.editErrors.get(rowKey);
  const _serverErrors = props.tableState.serverErrors.get(rowKey);
  const editErrors =
    _clientErrors || _serverErrors ? { ..._serverErrors, ..._clientErrors } : undefined;
  const rowSelectable = isRowSelectable(row, props.internalSchema.features.selection.selectable);

  return (
    <RowRenderer
      key={reactKey}
      row={row}
      rowIndex={rowIndex}
      columns={props.visibleColumns}
      registry={props.registry}
      namespace={props.namespace}
      showCheckbox={props.showSelectionColumn}
      rowSelectable={rowSelectable}
      isSelected={props.effectiveSelectedKeys.includes(rowKey)}
      onSelect={(checked) => {
        if (rowSelectable) props.handleRowSelect(rowKey, checked);
      }}
      isGroupChild={props.isGroupingEnabled}
      rowActions={
        props.isGroupingEnabled ? props.effectiveSubRowActions : props.internalSchema.actions.row
      }
      onExecuteAction={props.handleRowActionExec}
      checkPermission={props.checkPermission}
      subRowConfig={props.subRowConfig}
      subRowColumns={props.subRowColumns}
      editingRow={editingRow}
      onCellDoubleClick={props.hasEditableColumns ? props.handleCellDoubleClick : undefined}
      onEditChange={
        editingRow ? (field, value) => props.handleEditChange(rowKey, field, value) : undefined
      }
      onEditBlur={editingRow ? (field) => props.handleEditBlur(rowKey, field) : undefined}
      editErrors={editErrors}
      injectedData={props.injectedData}
      subRowInjectedData={props.subRowInjectedData}
      keyField={props.keyField}
      striped={props.internalSchema.features.rows.striped}
      editingSubRows={props.editingSubRows}
      subRowEditErrors={props.subRowEditErrors}
      onSubRowCellDoubleClick={props.handleSubRowCellDoubleClick}
      onSubRowEditChange={props.handleSubRowEditChange}
      onSubRowEditBlur={props.handleSubRowEditBlur}
      subRowActions={props.effectiveSubRowActions}
      expandedRows={props.tableState.expandedRows}
      onToggleExpand={props.handleToggleRowExpand}
      showActionsColumn={props.showActionsColumn}
      onRowSave={props.handleRowSave}
      onRowCancel={props.handleRowCancel}
      saveLoading={props.effectiveSaveLoading}
      externalEditControl={props.externalEditControl}
      parentColCount={
        (props.showSelectionColumn ? 1 : 0) +
        props.visibleColumns.length +
        (props.showActionsColumn ? 1 : 0)
      }
      subRowOverflowCols={props.subRowOverflowCols}
      pinOffsets={props.pinOffsets}
      rowStyleFn={props.rowStyleFn}
      onRowClick={props.onRowClick}
    />
  );
}

function renderPinnedTbody(
  rows: unknown[],
  position: 'top' | 'bottom',
  props: RowRenderSharedProps
) {
  if (rows.length === 0) return null;
  const stickyStyle: React.CSSProperties =
    position === 'top'
      ? { position: 'sticky', top: 0, zIndex: 2 }
      : { position: 'sticky', bottom: 0, zIndex: 2 };
  return (
    <tbody style={stickyStyle} data-testid={`dt-v2-pinned-${position}`}>
      {rows.map((row, idx) => {
        const rec = row as Record<string, unknown>;
        const rowKey = props.resolveRowKey(rec, idx);
        const reactKey = props.resolveReactKey(rec, idx);
        const editingRow = props.tableState.editingRows.get(rowKey) ?? null;
        const clientErrors = props.tableState.editErrors.get(rowKey);
        const serverErrors = props.tableState.serverErrors.get(rowKey);
        const editErrors =
          clientErrors || serverErrors ? { ...serverErrors, ...clientErrors } : undefined;
        const rowSelectable = isRowSelectable(
          rec,
          props.internalSchema.features.selection.selectable
        );
        return (
          <RowRenderer
            key={`pinned-${position}-${reactKey}`}
            row={rec}
            rowIndex={idx}
            columns={props.visibleColumns}
            registry={props.registry}
            namespace={props.namespace}
            showCheckbox={props.showSelectionColumn}
            rowSelectable={rowSelectable}
            isSelected={props.effectiveSelectedKeys.includes(rowKey)}
            onSelect={(checked) => {
              if (rowSelectable) props.handleRowSelect(rowKey, checked);
            }}
            isGroupChild={false}
            rowActions={props.internalSchema.actions.row}
            onExecuteAction={props.handleRowActionExec}
            checkPermission={props.checkPermission}
            subRowConfig={props.subRowConfig}
            subRowColumns={props.subRowColumns}
            editingRow={editingRow}
            onCellDoubleClick={props.hasEditableColumns ? props.handleCellDoubleClick : undefined}
            onEditChange={
              editingRow
                ? (field, value) => props.handleEditChange(rowKey, field, value)
                : undefined
            }
            onEditBlur={editingRow ? (field) => props.handleEditBlur(rowKey, field) : undefined}
            editErrors={editErrors}
            injectedData={props.injectedData}
            subRowInjectedData={props.subRowInjectedData}
            keyField={props.keyField}
            striped={false}
            editingSubRows={props.editingSubRows}
            subRowEditErrors={props.subRowEditErrors}
            onSubRowCellDoubleClick={props.handleSubRowCellDoubleClick}
            onSubRowEditChange={props.handleSubRowEditChange}
            onSubRowEditBlur={props.handleSubRowEditBlur}
            subRowActions={props.effectiveSubRowActions}
            expandedRows={props.tableState.expandedRows}
            onToggleExpand={props.handleToggleRowExpand}
            showActionsColumn={props.showActionsColumn}
            onRowSave={props.handleRowSave}
            onRowCancel={props.handleRowCancel}
            saveLoading={props.effectiveSaveLoading}
            externalEditControl={props.externalEditControl}
            parentColCount={
              (props.showSelectionColumn ? 1 : 0) +
              props.visibleColumns.length +
              (props.showActionsColumn ? 1 : 0)
            }
            subRowOverflowCols={props.subRowOverflowCols}
            pinOffsets={props.pinOffsets}
            onRowClick={props.onRowClick}
          />
        );
      })}
    </tbody>
  );
}

export function TableBodyRenderer(props: TableBodyRendererProps): React.ReactElement {
  const {
    renderData,
    paginatedData,
    topPinnedRows,
    bottomPinnedRows,
    shouldVirtualize,
    virtualRows,
    groupByConfig,
    groupAlwaysExpanded,
    isRandomAccessMode = false,
    randomAccessTotalRecords = 0,
    isAwaitingFilter = false,
    emptyFilterMessage,
    ...sharedProps
  } = props;

  const colSpan =
    sharedProps.visibleColumns.length +
    (sharedProps.showSelectionColumn ? 1 : 0) +
    (sharedProps.showActionsColumn ? 1 : 0) +
    sharedProps.subRowOverflowCols;

  const isEmpty = isRandomAccessMode
    ? randomAccessTotalRecords === 0
    : paginatedData.length === 0;

  const virtualRenderRow = useCallback((group: VirtualRowGroup, _vIndex: number) => {
    const rowIndex = group.dataIndex;
    const row = group.row;

    if (group.type === 'skeleton') {
      return (
        <SkeletonRow
          key={`skel_${rowIndex}`}
          colSpan={colSpan}
          columns={sharedProps.visibleColumns}
          showCheckbox={sharedProps.showSelectionColumn}
          showActions={sharedProps.showActionsColumn}
          height={sharedProps.internalSchema.features.virtualScroll.estimatedRowHeight}
        />
      );
    }

    if (group.type === 'group') {
      const groupRow = row as GroupedRow;
      return (
        <GroupRowRenderer
          key={`group-${groupRow.__groupKey}`}
          groupRow={groupRow}
          columns={sharedProps.visibleColumns}
          showCheckbox={sharedProps.showSelectionColumn}
          showActionsColumn={sharedProps.showActionsColumn}
          groupByField={groupByConfig?.fields[0] ?? ''}
          namespace={sharedProps.namespace}
          onToggleExpand={(groupKey) => sharedProps.stateActions.toggleGroup(groupKey)}
          hideGroupedColumn={groupByConfig?.hideGroupedColumn ?? false}
          hideExpandButton={groupAlwaysExpanded}
          rowActions={sharedProps.internalSchema.actions.row}
          onExecuteAction={sharedProps.handleRowActionExec}
          checkPermission={sharedProps.checkPermission}
          subRowOverflowCols={sharedProps.subRowOverflowCols}
        />
      );
    }

    const rawRec = row as Record<string, unknown>;
    const groupedField = rawRec.__groupedField as string | undefined;
    const rec = groupedField ? { ...rawRec, [groupedField]: undefined } : rawRec;
    return renderSingleRow(rec, rawRec, rowIndex, sharedProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colSpan, sharedProps, groupByConfig, groupAlwaysExpanded]);

  return (
    <React.Fragment>
      {renderPinnedTbody(topPinnedRows, 'top', sharedProps)}
      {isEmpty ? (
        <tbody>
          <tr className='border-0'>
            <td colSpan={colSpan} className='border-b-0' id={TABLE_IDS.emptyState} data-testid='dt-v2-empty-state'>
              <EmptyState
                size='md'
                icon={isAwaitingFilter ? 'filter_alt' : 'info'}
                title={isAwaitingFilter ? (emptyFilterMessage ?? 'Search or apply filters to view results.') : 'No Data Found'}
                semanticId={TABLE_IDS.emptyState}
              />
            </td>
          </tr>
        </tbody>
      ) : shouldVirtualize ? (
        <VirtualTbody
          virtualRows={virtualRows}
          colSpan={colSpan}
          renderRow={virtualRenderRow}
        />
      ) : (
        <tbody>
          {renderData.map((row, rowIndex) => {
            if (isGroupRow(row)) {
              const groupRow = row as GroupedRow;
              return (
                <GroupRowRenderer
                  key={`group-${groupRow.__groupKey}`}
                  groupRow={groupRow}
                  columns={sharedProps.visibleColumns}
                  showCheckbox={sharedProps.showSelectionColumn}
                  showActionsColumn={sharedProps.showActionsColumn}
                  groupByField={groupByConfig?.fields[0] ?? ''}
                  namespace={sharedProps.namespace}
                  onToggleExpand={(groupKey) => sharedProps.stateActions.toggleGroup(groupKey)}
                  hideGroupedColumn={groupByConfig?.hideGroupedColumn ?? false}
                  hideExpandButton={groupAlwaysExpanded}
                  rowActions={sharedProps.internalSchema.actions.row}
                  onExecuteAction={sharedProps.handleRowActionExec}
                  checkPermission={sharedProps.checkPermission}
                  subRowOverflowCols={sharedProps.subRowOverflowCols}
                />
              );
            }

            const rawRec = row as Record<string, unknown>;
            const groupedField = rawRec.__groupedField as string | undefined;
            const rec = groupedField ? { ...rawRec, [groupedField]: undefined } : rawRec;
            return renderSingleRow(rec, rawRec, rowIndex, sharedProps);
          })}
        </tbody>
      )}
      {renderPinnedTbody(bottomPinnedRows, 'bottom', sharedProps)}
    </React.Fragment>
  );
}
